/**
 * EventBadge — compact pill identifying an event by type, with optional
 * severity decoration. Used in MonthGrid dots, AgendaList rows and the
 * detail panel header.
 *
 * Props:
 *   - type: "payment" | "task" | "milestone" | "delivery"
 *   - severity?: "high" | "medium" | "low"
 *   - size?: "xs" | "sm" | "md"  (default "sm")
 *   - children?: ReactNode  (label, optional)
 *
 * Design intent: payments use the warm accent (most easy to recognise),
 * tasks use a neutral slate, milestones an olive, deliveries a deep teal.
 * High severity adds a subtle ring, never floods the calendar in red.
 */
import React from "react";
import { Coin, ListChecks, Flag, Key, Handshake } from "@phosphor-icons/react";

const TYPE_STYLE = {
  payment:   { bg: "bg-[#9C5236]/10", fg: "text-[#9C5236]", icon: Coin,        label: "Pago" },
  task:      { bg: "bg-stone-200",     fg: "text-stone-700", icon: ListChecks,  label: "Tarea" },
  milestone: { bg: "bg-emerald-100",   fg: "text-emerald-800", icon: Flag,      label: "Hito" },
  delivery:  { bg: "bg-teal-100",      fg: "text-teal-800",  icon: Key,         label: "Entrega" },
  meeting:   { bg: "bg-cyan-100",      fg: "text-cyan-800",  icon: Handshake,   label: "Reunión" },
};

const SIZE = {
  xs: { px: "px-1.5", py: "py-0.5", text: "text-[10px]", icon: 10, gap: "gap-1" },
  sm: { px: "px-2",   py: "py-0.5", text: "text-xs",     icon: 12, gap: "gap-1.5" },
  md: { px: "px-2.5", py: "py-1",   text: "text-sm",     icon: 14, gap: "gap-1.5" },
};

export default function EventBadge({ type, severity, size = "sm", children }) {
  const t = TYPE_STYLE[type] || TYPE_STYLE.task;
  const s = SIZE[size] || SIZE.sm;
  const Icon = t.icon;
  const ring = severity === "high" ? "ring-1 ring-red-500/40" : "";
  return (
    <span
      className={`inline-flex items-center ${s.gap} rounded-full ${s.px} ${s.py} ${s.text} ${t.bg} ${t.fg} ${ring}`}
      data-testid={`event-badge-${type}`}
    >
      <Icon size={s.icon} weight="bold" />
      {children !== undefined ? children : t.label}
    </span>
  );
}

export const EVENT_TYPES = ["payment", "task", "milestone", "delivery", "meeting"];
export const EVENT_TYPE_LABEL = {
  payment: "Pagos",
  task: "Tareas",
  milestone: "Hitos",
  delivery: "Entregas",
  meeting: "Reuniones",
};
export { TYPE_STYLE };
